import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import TagInput from "../components/TagInput";
import TinyMceEditor from "../components/TinyMceEditor";
import { apiUrl, requestWithToken } from "../utils/apis";
import { bucketUrl } from "../utils/helper";
import { storyMapDetail } from "../action/storyMaps";
import trash from "../assets/trash.svg";
import infoCircle from "../assets/info-circle.svg";
import MediaPickerModal from "../components/MediaPickerModal";
import { fetchBlogCategoryList } from "../action/formaction";
import SwitchToggle from "../components/switch";
import moment from "moment";
import { userList } from "../action/user";
import LocationLanguageSpecific from "../components/locationLanguageSpecific";

const emptyStop = () => ({
  title: "",
  location: "",
  content: "",
  map_url: "",
  map_input_type: "url",
  file: null,
});

const normalizeProjectUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    if (
      parsed.hostname === "share.geolibre.app" &&
      !parsed.pathname.toLowerCase().endsWith(".geolibre.json")
    ) {
      parsed.pathname = parsed.pathname.replace(/\/+$/, "") + ".geolibre.json";
      return parsed.toString();
    }
  } catch {
    return rawUrl;
  }
  return rawUrl;
};

const getPublicMapUrl = (mapUrl) => {
  if (!mapUrl) return "";
  if (/^https?:\/\//i.test(mapUrl)) {
    return normalizeProjectUrl(mapUrl);
  }
  return `${bucketUrl}${mapUrl}`;
};

const buildMapEmbedUrl = (mapUrl) => {
  const projectUrl = getPublicMapUrl(mapUrl);
  return projectUrl
    ? `https://web.geolibre.app/?url=${encodeURIComponent(projectUrl)}&maponly`
    : "";
};

const injectCenterMarker = (projectJson) => {
  // Ensure we have a valid, working public basemap style to display street maps
  const currentBasemap = projectJson?.basemapStyleUrl || "";
  const isInvalidStyle =
    !currentBasemap ||
    currentBasemap.includes("demotiles.maplibre.org") ||
    currentBasemap.includes("cartocdn.com") ||
    currentBasemap.includes("mapbox.com") ||
    currentBasemap.includes("maptiler.com");

  const updatedProject = {
    ...projectJson,
    name: projectJson.name ? `${projectJson.name}-${Date.now()}` : `Map-${Date.now()}`,
    basemapStyleUrl: isInvalidStyle
      ? "https://tiles.openfreemap.org/styles/liberty"
      : currentBasemap,
    basemapVisible: projectJson.basemapVisible !== undefined ? projectJson.basemapVisible : true,
    basemapOpacity: projectJson.basemapOpacity !== undefined ? projectJson.basemapOpacity : 1,
  };

  const center = updatedProject?.mapView?.center;
  if (!Array.isArray(center) || center.length !== 2) {
    return updatedProject;
  }

  const alreadyHasMarker = (updatedProject.layers || []).some(
    (layer) => layer.name === "Location Marker"
  );
  if (alreadyHasMarker) {
    return updatedProject;
  }

  const markerLayer = {
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `marker-${Date.now()}`,
    name: "Location Marker",
    type: "geojson",
    source: { type: "geojson" },
    visible: true,
    opacity: 1,
    style: {
      minZoom: 0,
      maxZoom: 24,
      fillColor: "#e11d48",
      strokeColor: "#ffffff",
      strokeWidth: 2,
      fillOpacity: 1,
      circleRadius: 8,
    },
    metadata: {},
    geojson: {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: { type: "Point", coordinates: center },
        },
      ],
    },
  };

  return {
    ...updatedProject,
    layers: [...(updatedProject.layers || []), markerLayer],
  };
};

const uploadGeoLibreProjectFile = async (file) => {
  let uploadFile = file;
  let uploadName = file.name;
  if (!uploadName.toLowerCase().endsWith(".json")) {
    uploadName += ".json";
  }

  try {
    const projectJson = JSON.parse(await file.text());
    const withMarker = injectCenterMarker(projectJson);
    uploadFile = new File([JSON.stringify(withMarker)], uploadName, {
      type: "application/json",
    });
  } catch {
    // If parsing/text extraction fails, we still create a new file with the renamed extension if necessary
    if (uploadFile.name !== uploadName) {
      uploadFile = new File([uploadFile], uploadName, {
        type: "application/json",
      });
    }
  }

  const formData = new FormData();
  formData.append("file", uploadFile);

  const response = await axios.post(`${apiUrl}/api/media/upload/maps?type=2`, formData, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const key = response?.data?.data?.[0];
  if (!key) {
    throw new Error("Upload succeeded but no file URL was returned");
  }
  return key;
};

const AddLocationBlog = () => {
  const navigate = useNavigate();
  const { storyMapId } = useParams();
  const [loadingButton, setLoadingButton] = useState(null);
  const [uploadingStopIndex, setUploadingStopIndex] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [banner, setBanner] = useState(null);
  const [categories, setCategories] = useState([]);
  const rightSectionRef = useRef(null);
  const [authorImage, setAuthorImage] = useState(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [storyMapData, setStoryMapData] = useState({});

  const fetchAuthors = async () => {
    try {
      const res = await userList(1, 100);
      if (res?.status) {
        const authorUsers = (res.data?.list || []).filter(
          (u) =>
            u?.roleData?.slug === "author" ||
            u?.roleData?.name?.toLowerCase() === "author" ||
            u?.role?.toLowerCase() === "author"
        );
        setAuthors(authorUsers);
      }
    } catch (e) {
      console.log("Failed to fetch authors", e);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchBlogCategoryList(1, 100);
      if (response?.status) {
        setCategories(response?.data?.list || []);
      }
    } catch (error) {
      toast.error("Failed to fetch blog categories");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAuthors();
  }, []);

  const formik = useFormik({
    initialValues: {
      title: "",
      pin: false,
      slug: "",
      author: "",
      publish_date: moment().format("YYYY-MM-DD"),
      banner: "",
      desc: "",
      blog_category_id: "",
      is_published: true,
      story_map: [emptyStop()],
      focus_keywords: [],
      metaTitle: "",
      metaDesc: "",
      og_tags: {
        title: "",
        desc: "",
        image: "",
      },
      extras: {
        author_image: "",
      },
      locale: "en",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      slug: Yup.string()
        .required("Slug is required")
        .matches(
          /^[a-zA-Z0-9\u0900-\u097F_-]+$/,
          "Slug can contain Hindi, English letters, numbers, hyphens, and underscores"
        ),
      banner: Yup.string().required("Banner image is required"),
      story_map: Yup.array()
        .of(
          Yup.object({
            title: Yup.string().required("Location title is required"),
            location: Yup.string().required("Location is required"),
            content: Yup.string().required("Content is required"),
            map_url: Yup.string().required(
              "Upload a GeoLibre file or paste a GeoLibre URL"
            ),
          })
        )
        .min(1, "At least one location is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const bannerpath = values.banner
          ? values.banner.replace(process.env.REACT_APP_BUCKET_URL || bucketUrl || "", "")
          : null;

        const payload = {
          title: values.title,
          pin: values.pin,
          slug: values.slug,
          author_name: values.author,
          publish_date: moment(values.publish_date).toISOString(),
          desc: values.desc,
          blog_category_id: values.blog_category_id || null,
          assets: values.banner
            ? {
              type: 1, // 1 = image
              url: bannerpath,
              w: "0",
              h: "0",
            }
            : {},
          is_published: values.is_published,
          story_map: values.story_map.map((stop) => ({
            title: stop.title,
            location: stop.location,
            content: stop.content,
            map_url: normalizeProjectUrl(stop.map_url),
          })),
          seo: {
            metaTitle: values.metaTitle,
            metaDesc: values.metaDesc,
            focus_keywords: values.focus_keywords.join(","),
          },
          og_tags: values.og_tags,
          extras: values.extras,
          locale: values.locale || "en",
        };

        const response = storyMapId
          ? await requestWithToken("PUT", `/api/story-maps/${storyMapId}`, payload)
          : await requestWithToken("POST", "/api/story-maps", payload);

        if (response?.status === 200) {
          toast.success(
            storyMapId
              ? "Location Blog Updated Successfully!"
              : "Location Blog Created Successfully!"
          );
          navigate("/location-blogs");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
          "Something went wrong. Please try again."
        );
        if (loadingButton === "toggle") {
          formik.setFieldValue("is_published", !formik.values.is_published);
        }
      } finally {
        setSubmitting(false);
        setLoadingButton(null);
      }
    },
  });

  useLayoutEffect(() => {
    if (!rightSectionRef.current) return;

    const element = rightSectionRef.current;

    const updateHeight = () => {
      const height = element.offsetHeight;
      document.documentElement.style.setProperty(
        "--right-section-height",
        `${height}px`
      );
    };

    updateHeight();

    const ro = new ResizeObserver(updateHeight);
    ro.observe(element);

    return () => ro.disconnect();
  }, [formik.values.story_map]);

  useEffect(() => {
    const fetchDetails = async (id) => {
      try {
        const response = await storyMapDetail(id);
        if (response?.status) {
          const data = response.data || {};
          const bucket = process.env.REACT_APP_BUCKET_URL || bucketUrl || "";
          console.log("[StoryMap API Response]", response, "\n[data keys]", Object.keys(data));
          setStoryMapData(data);
          formik.setValues({
            title: data.title || "",
            pin: data.pin || false,
            slug: data.slug || "",
            author: data.author_name || "",
            publish_date: data.publish_date ? moment(data.publish_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
            desc: data.desc || "",
            blog_category_id: data.blog_category?._id || data.blog_category_id || "",
            banner: data.assets?.url ? bucket + data.assets.url : "",
            is_published: data.is_published || false,
            story_map:
              data.story_map?.length > 0
                ? data.story_map.map((stop) => ({
                  title: stop.title || "",
                  location: stop.location || "",
                  content: stop.content || "",
                  map_url: stop.map_url || "",
                  map_input_type: /^https?:\/\//i.test(stop.map_url || "")
                    ? "url"
                    : "file",
                  file: null,
                }))
                : [emptyStop()],
            focus_keywords: data.seo?.focus_keywords
              ? data.seo.focus_keywords.split(",").filter(Boolean)
              : [],
            metaTitle: data.seo?.metaTitle || "",
            metaDesc: data.seo?.metaDesc || "",
            og_tags: {
              title: data.og_tags?.title || "",
              desc: data.og_tags?.desc || "",
              image: data.og_tags?.image || "",
            },
            extras: data.extras || { author_image: "" },
            locale: data.locale || "en",
          });
          setBanner(data.assets?.url ? bucket + data.assets.url : null);
          setAuthorImage(data.extras?.author_image ? bucket + data.extras.author_image : null);
        }
      } catch (error) {
        toast.error("Failed to fetch location blog details");
      }
    };

    if (storyMapId) {
      fetchDetails(storyMapId);
    }
    // Formik helpers are intentionally excluded so edit-mode hydration only
    // runs when the route id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyMapId]);

  const setStopValue = (index, key, value) => {
    formik.setFieldValue(`story_map.${index}.${key}`, value);
  };

  const addStop = () => {
    formik.setFieldValue("story_map", [...formik.values.story_map, emptyStop()]);
  };

  const removeStop = (index) => {
    if (formik.values.story_map.length === 1) return;
    formik.setFieldValue(
      "story_map",
      formik.values.story_map.filter((_, i) => i !== index)
    );
  };

  const handleMapFileChange = async (index, file) => {
    if (!file) return;
    const nameLower = file.name.toLowerCase();
    if (!nameLower.endsWith(".json") && !nameLower.endsWith(".geolibre")) {
      toast.error("Please upload a GeoLibre project file (.geolibre.json)");
      return;
    }

    setStopValue(index, "file", file);
    setUploadingStopIndex(index);
    try {
      const key = await uploadGeoLibreProjectFile(file);
      setStopValue(index, "map_url", key);
      toast.success("GeoLibre project uploaded");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error.message ||
        "Failed to upload GeoLibre project"
      );
    } finally {
      setUploadingStopIndex(null);
    }
  };

  const normalizeStopUrl = (index) => {
    const current = formik.values.story_map[index]?.map_url || "";
    const normalized = normalizeProjectUrl(current.trim());
    setStopValue(index, "map_url", normalized);
  };

  const handlePublishToggle = async () => {
    setLoadingButton("toggle");
    formik.setFieldValue("is_published", !formik.values.is_published);
    await formik.submitForm();
  };

  const handleSaveOrUpdate = async () => {
    setLoadingButton("save");
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    } else {
      formik.setTouched({
        title: true,
        slug: true,
        banner: true,
        story_map: formik.values.story_map.map(() => ({
          title: true,
          location: true,
          content: true,
          map_url: true,
        })),
      });
      setLoadingButton(null);
    }
  };

  const stopErrors = (index) => formik.errors.story_map?.[index] || {};
  const stopTouched = (index) => formik.touched.story_map?.[index] || {};

  return (
    <>
      <PageHeader
        pageTitle={storyMapId ? "Location Blog Details" : "Add Location Blog"}
      >
        <button
          className="btn-white"
          onClick={handlePublishToggle}
          disabled={loadingButton === "toggle" && formik.isSubmitting}
        >
          {loadingButton === "toggle" && formik.isSubmitting ? (
            <Spinner size="sm" />
          ) : formik.values.is_published ? (
            storyMapId ? "Unpublish" : "Save Draft"
          ) : (
            "Publish"
          )}
        </button>
        <button
          className="btn-black"
          onClick={handleSaveOrUpdate}
          disabled={loadingButton === "save" && formik.isSubmitting}
        >
          {loadingButton === "save" && formik.isSubmitting ? (
            <Spinner size="sm" />
          ) : storyMapId ? (
            "Update"
          ) : (
            "Publish"
          )}
        </button>
      </PageHeader>

      <div className="page-container">
        <div className="add-blog-form">
          <div className="row">
            <div className="col-md-8 d-flex flex-column">
              {/* Story Locations Card */}
              <div className="theme-card p-4 mb-3">
                <div className="d-flex align-items-center justify-content-between pb-3">
                  <div className="card-title">Story Locations</div>
                  <button type="button" className="btn-black" onClick={addStop}>
                    Add Location
                  </button>
                </div>

                {formik.values.story_map.map((stop, index) => {
                  const errors = stopErrors(index);
                  const touched = stopTouched(index);
                  const previewUrl = buildMapEmbedUrl(stop.map_url);

                  return (
                    <div
                      className="location-story-card mb-3"
                      key={`story-map-stop-${index}`}
                    >
                      <div className="location-story-card__header">
                        <div>
                          <div className="location-story-card__eyebrow">
                            Story stop
                          </div>
                          <div className="location-story-card__title">
                            Location {index + 1}
                          </div>
                        </div>
                        {formik.values.story_map.length > 1 && (
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={() => removeStop(index)}
                          >
                            <img src={trash} alt="" />
                          </button>
                        )}
                      </div>

                      <div className="location-story-card__grid">
                        <div className="frm-group">
                          <label className="frm-label">Location Title</label>
                          <input
                            type="text"
                            className="frm-input"
                            placeholder="Seattle Narrative"
                            value={stop.title}
                            onChange={(e) =>
                              setStopValue(index, "title", e.target.value)
                            }
                          />
                          {touched.title && errors.title && (
                            <div className="input-error">{errors.title}</div>
                          )}
                        </div>

                        <div className="frm-group">
                          <label className="frm-label">Location</label>
                          <input
                            type="text"
                            className="frm-input"
                            placeholder="Seattle, WA"
                            value={stop.location}
                            onChange={(e) =>
                              setStopValue(index, "location", e.target.value)
                            }
                          />
                          {touched.location && errors.location && (
                            <div className="input-error">{errors.location}</div>
                          )}
                        </div>
                      </div>

                      <div className="frm-group">
                        <label className="frm-label">Content</label>
                        <div className="location-story-card__editor">
                          <TinyMceEditor
                            value={stop.content}
                            onChange={(content) =>
                              setStopValue(index, "content", content)
                            }
                            height={650}
                            enableMap={false}
                          />
                        </div>
                        {touched.content && errors.content && (
                          <div className="input-error">{errors.content}</div>
                        )}
                      </div>

                      <div className="frm-group">
                        <div className="location-story-card__map-head">
                          <label className="frm-label mb-0">GeoLibre Map</label>
                          <div className="location-map-mode" role="group">
                            <button
                              type="button"
                              className={
                                stop.map_input_type === "url" ? "active" : ""
                              }
                              onClick={() => {
                                setStopValue(index, "map_input_type", "url");
                                setStopValue(index, "map_url", "");
                                setStopValue(index, "file", null);
                              }}
                            >
                              Share URL
                            </button>
                            <button
                              type="button"
                              className={
                                stop.map_input_type === "file" ? "active" : ""
                              }
                              onClick={() => {
                                setStopValue(index, "map_input_type", "file");
                                setStopValue(index, "map_url", "");
                                setStopValue(index, "file", null);
                              }}
                            >
                              Upload File
                            </button>
                          </div>
                        </div>

                        {stop.map_input_type === "url" ? (
                          <input
                            type="text"
                            className="frm-input"
                            placeholder="https://share.geolibre.app/user/project"
                            value={stop.map_url}
                            onChange={(e) =>
                              setStopValue(index, "map_url", e.target.value)
                            }
                            onBlur={() => normalizeStopUrl(index)}
                          />
                        ) : (
                          <div className="location-file-upload">
                            <input
                              id={`story-map-file-${index}`}
                              type="file"
                              accept=".json,.geolibre"
                              onChange={(e) =>
                                handleMapFileChange(index, e.target.files?.[0])
                              }
                            />
                            <label htmlFor={`story-map-file-${index}`}>
                              Choose GeoLibre File
                            </label>
                            <span>
                              {stop.file?.name ||
                                (stop.map_url
                                  ? "Uploaded project ready"
                                  : "No file chosen")}
                            </span>
                            {uploadingStopIndex === index && (
                              <span>
                                <Spinner size="sm" /> Uploading...
                              </span>
                            )}
                          </div>
                        )}

                        {touched.map_url && errors.map_url && (
                          <div className="input-error">{errors.map_url}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-md-4 position-relative">
              <div className="right-sec" ref={rightSectionRef}>
                {/* Blog General Details Card */}
                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">General Details</div>

                  {/* Title */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Blog Title
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Blog Title"
                      />
                    </label>
                    <input
                      type="text"
                      name="title"
                      className="frm-input"
                      placeholder="Enter Blog Title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className="input-error">{formik.errors.title}</div>
                    )}
                  </div>

                  {/* Pin */}
                  <div className="frm-group">
                    <div className="switch-btn px-0">
                      <label className="frm-label mb-0">Pin</label>
                      <SwitchToggle
                        checked={formik.values.pin}
                        onChange={(e) =>
                          formik.setFieldValue("pin", e.target.checked)
                        }
                      />
                    </div>
                  </div>

                  {/* Slug */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Slug
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Slug name eg. my-first-blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="slug"
                      className="frm-input"
                      placeholder="Slug name eg. my-first-blog"
                      value={formik.values.slug}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.slug && formik.errors.slug && (
                      <div className="input-error">{formik.errors.slug}</div>
                    )}
                  </div>

                  {/* Author Name */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Author Name
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Author name of Blog"
                      />
                    </label>
                    <select
                      name="author"
                      className="frm-select"
                      value={formik.values.author}
                      onChange={(e) => {
                        const selectedName = e.target.value;
                        formik.setFieldValue("author", selectedName);
                        const matchedAuthor = authors.find(a => a.name === selectedName);
                        if (matchedAuthor) {
                          if (matchedAuthor.pic) {
                            setAuthorImage(matchedAuthor.pic);
                            const imagePath = matchedAuthor.pic.replace(process.env.REACT_APP_BUCKET_URL || bucketUrl || "", "");
                            formik.setFieldValue("extras.author_image", imagePath);
                          } else {
                            setAuthorImage(null);
                            formik.setFieldValue("extras.author_image", "");
                          }
                        }
                      }}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Author</option>
                      {authors.map((auth) => (
                        <option key={auth._id} value={auth.name}>
                          {auth.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div className="frm-group">
                    <label className="frm-label">Author Image</label>

                    <div
                      className="pointer"
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowImagePicker(true)}
                    >
                      {authorImage ? (
                        <img
                          src={authorImage}
                          alt="image preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            background: "#f5f5f5",
                            color: "#777",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          Upload Image
                        </div>
                      )}
                    </div>
                  </div> */}


                  {/* Publish Date */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Publish Date
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Select publish date"
                      />
                    </label>
                    <input
                      type="date"
                      name="publish_date"
                      className="frm-input"
                      value={formik.values.publish_date}
                      onChange={formik.handleChange}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  </div>

                  {/* Blog Banner */}
                  <div className="frm-group">
                    <label className="frm-label">Blog Banner</label>
                    <div
                      className="pointer"
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowMediaPicker(true)}
                    >
                      {banner ? (
                        <img
                          src={banner}
                          alt="banner preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            background: "#f5f5f5",
                            color: "#777",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          Upload Banner
                        </div>
                      )}
                    </div>
                    {formik.touched.banner && formik.errors.banner && (
                      <div className="input-error">{formik.errors.banner}</div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Description
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add description of Blog"
                      />
                    </label>
                    <textarea
                      name="desc"
                      className="frm-input"
                      placeholder="Enter Description"
                      rows={4}
                      value={formik.values.desc}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  {/* Blog Category */}
                  {categories.length > 0 && (
                    <div className="frm-group">
                      <label className="frm-label d-flex align-items-center">
                        Blog Category
                      </label>
                      <div className="sel-box ms-auto">
                        <select
                          name="blog_category_id"
                          id="selmenu"
                          className="theme-select"
                          onChange={formik.handleChange}
                          value={formik.values?.blog_category_id || ""}
                        >
                          <option value="">Select Category</option>
                          {categories.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">SEO</div>
                  <div className="frm-group">
                    <label className="frm-label">Focus Keywords</label>
                    <div className="tag-card">
                      <TagInput
                        value={formik.values.focus_keywords}
                        onChange={(tags) =>
                          formik.setFieldValue("focus_keywords", tags)
                        }
                      />
                    </div>
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">SEO Title</label>
                    <input
                      type="text"
                      name="metaTitle"
                      className="frm-input"
                      placeholder="Scrollytelling Tour of Five Cities"
                      value={formik.values.metaTitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">Meta Description</label>
                    <textarea
                      name="metaDesc"
                      className="frm-input"
                      rows={3}
                      placeholder="An interactive map journey through five great cities."
                      value={formik.values.metaDesc}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>

                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">OG Tags</div>
                  <div className="frm-group">
                    <label className="frm-label">OG Title</label>
                    <input
                      type="text"
                      name="og_tags.title"
                      className="frm-input"
                      placeholder="Scrollytelling Tour"
                      value={formik.values.og_tags.title}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">OG Description</label>
                    <textarea
                      name="og_tags.desc"
                      className="frm-input"
                      rows={3}
                      placeholder="Map journey through five cities."
                      value={formik.values.og_tags.desc}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">OG Image URL</label>
                    <input
                      type="text"
                      name="og_tags.image"
                      className="frm-input"
                      placeholder="Optional image URL"
                      value={formik.values.og_tags.image}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>

                <LocationLanguageSpecific
                  storyMapId={storyMapId}
                  storyMapData={storyMapData}
                  setStoryMapData={setStoryMapData}
                />

              </div>
            </div>
          </div>
        </div>
      </div>
      {showMediaPicker && (
        <MediaPickerModal
          show={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(mediaUrl) => {
            setBanner(mediaUrl);
            formik.setFieldValue("banner", mediaUrl);
            setShowMediaPicker(false);
          }}
        />
      )}
      {showImagePicker && (
        <MediaPickerModal
          show={showImagePicker}
          onClose={() => setShowImagePicker(false)}
          onSelect={(mediaUrl) => {
            const imagePath = mediaUrl.replace(process.env.REACT_APP_BUCKET_URL || bucketUrl || "", "");
            setAuthorImage(mediaUrl);
            formik.setFieldValue("extras.author_image", imagePath);
            setShowImagePicker(false);
          }}
        />
      )}
    </>
  );
};

export default AddLocationBlog;
